import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import billingService from '../../services/BillingService.js';
import locationService from '../../services/LocationService.js';
import tankCleaningService from '../../services/TankCleaningService.js';
import dryBoxSurveyService from '../../services/DryBoxSurveyService.js';
import tiReportService from '../../services/TIReportService.js';
import eiReceiptService from '../../services/EIReceiptService.js';
import onhireService from '../../services/OnHireService.js';
import {
  FileText, ArrowLeft, Save, Building2, Phone, Mail,
  MapPin, Tag, Wrench, Package, FileCheck, ReceiptText,
  Anchor, AlertCircle, CheckCircle, Loader2, IndianRupee,
  Plus, Trash2, X, ChevronDown, ChevronUp, ClipboardList
} from 'lucide-react';

// ─── Constants ──────────────────────────────────────────────────────────────

const BILLING_TYPES = ['SURVEY', 'SERVICE', 'INSPECTION', 'OTHER'];

// Maps frontend filter keys → API response keys
const SERVICE_TYPE_MAP = {
  'tank-cleaning': 'tank_cleanings',
  'dry-box':       'dry_boxes',
  'ti-report':     'ti_reports',
  'onhire':        'onhires',
  'ei-receipt':    'ei_receipts',
};

// Maps frontend filter keys → billing model ID-array field names
const SERVICE_IDS_FIELD_MAP = {
  'tank-cleaning': 'tank_cleaning_ids',
  'dry-box':       'dry_box_ids',
  'ti-report':     'ti_report_ids',
  'onhire':        'onhire_ids',
  'ei-receipt':    'ei_receipt_ids',
};

// Maps frontend filter keys → client rates field names
const SERVICE_RATE_MAP = {
  'tank-cleaning': 'tank_cleaning_rate',
  'dry-box':       'dry_box_rate',
  'ti-report':     'ti_report_rate',
  'onhire':        'onhire_rate',
  'ei-receipt':    'ei_receipt_rate',
};

const SERVICE_LABELS = {
  'tank-cleaning': 'Tank Cleaning',
  'dry-box':       'Dry Box',
  'ti-report':     'TI Report',
  'onhire':        'On Hire',
  'ei-receipt':    'EI Receipt',
};

const SERVICE_CONFIG = {
  tank_cleaning: {
    label: 'Tank Cleaning',
    icon: Wrench,
    color: 'blue',
    idField: 'tank_cleaning_ids',
    qtyField: 'tank_cleaning_qty',
    amountField: 'tank_cleaning_amount',
    rateField: 'tank_cleaning_rate',
    key: 'tank-cleaning'
  },
  dry_box: {
    label: 'Dry Box',
    icon: Package,
    color: 'green',
    idField: 'dry_box_ids',
    qtyField: 'dry_box_qty',
    amountField: 'dry_box_amount',
    rateField: 'dry_box_rate',
    key: 'dry-box'
  },
  ti_report: {
    label: 'TI Report',
    icon: FileCheck,
    color: 'purple',
    idField: 'ti_report_ids',
    qtyField: 'ti_report_qty',
    amountField: 'ti_report_amount',
    rateField: 'ti_report_rate',
    key: 'ti-report'
  },
  ei_receipt: {
    label: 'EI Receipt',
    icon: ReceiptText,
    color: 'orange',
    idField: 'ei_receipt_ids',
    qtyField: 'ei_receipt_qty',
    amountField: 'ei_receipt_amount',
    rateField: 'ei_receipt_rate',
    key: 'ei-receipt'
  },
  onhire: {
    label: 'On Hire',
    icon: Anchor,
    color: 'teal',
    idField: 'onhire_ids',
    qtyField: 'onhire_qty',
    amountField: 'onhire_amount',
    rateField: 'onhire_rate',
    key: 'onhire'
  },
};

const COLOR_CLASSES = {
  blue:   { bg: 'bg-blue-50',   border: 'border-blue-200',   text: 'text-blue-700',   badge: 'bg-blue-100 text-blue-700',   icon: 'text-blue-500'   },
  green:  { bg: 'bg-green-50',  border: 'border-green-200',  text: 'text-green-700',  badge: 'bg-green-100 text-green-700', icon: 'text-green-500'  },
  purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-700', icon: 'text-purple-500' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', badge: 'bg-orange-100 text-orange-700', icon: 'text-orange-500' },
  teal:   { bg: 'bg-teal-50',   border: 'border-teal-200',   text: 'text-teal-700',   badge: 'bg-teal-100 text-teal-700',   icon: 'text-teal-500'   },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(amount || 0);

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
};

const getServiceLabel = (service) => {
  if (service.container_number) return service.container_number;
  if (service.booking_no) return service.booking_no;
  if (service.report_id) return service.report_id;
  if (service.receipt_id) return service.receipt_id;
  if (service.onhire_id) return service.onhire_id;
  return `ID: ${service.id}`;
};

// ─── Input Components ─────────────────────────────────────────────────────────

const FormLabel = ({ children, required }) => (
  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
    {children}
    {required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

const ReadOnlyField = ({ label, value }) => (
  <div>
    <FormLabel>{label}</FormLabel>
    <div className="w-full px-3 py-2.5 border border-gray-100 rounded-lg bg-gray-50 text-sm text-gray-600 font-medium min-h-[40px]">
      {value || <span className="text-gray-400">—</span>}
    </div>
    <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
      <span className="w-1.5 h-1.5 rounded-full bg-gray-300 inline-block" />
      Read-only
    </p>
  </div>
);

const inputClass = "w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-buttonBg/30 focus:border-buttonBg transition-colors bg-white";

// ─── Main Component ───────────────────────────────────────────────────────────

const EditBilling = () => {
  const { billingId } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    location_id:  '',
    billing_date: '',
    billing_type: 'SURVEY',
    description:  '',
    sac_code:     '',
    cgst:         0,
    sgst:         0,
    igst:         0,
    paid:         0,
    other_description: '',
    other_charges: 0,
    showOtherCharges: false,
    // Service selections
    selectedServices: {
      'tank-cleaning': [],
      'dry-box':       [],
      'ti-report':     [],
      'onhire':        [],
      'ei-receipt':    [],
    },
  });

  const [billing, setBilling] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Service management
  const [serviceRecords, setServiceRecords] = useState({});
  const [pendingServices, setPendingServices] = useState([]);
  const [clientRates, setClientRates] = useState({});
  const [showAddServices, setShowAddServices] = useState(false);
  const [serviceFilter, setServiceFilter] = useState('tank-cleaning');
  const [expandedServices, setExpandedServices] = useState({});

  // ─── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([fetchBilling(), fetchLocations()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingId]);

  const fetchBilling = async () => {
    try {
      setLoading(true);
      const response = await billingService.getById(billingId);
      const data = response.data;
      setBilling(data);
      
      // Set form data
      setFormData({
        location_id:  data.location_id  || '',
        billing_date: data.billing_date || '',
        billing_type: data.billing_type || 'SURVEY',
        description:  data.description  || '',
        sac_code:     data.sac_code     || '',
        cgst:         data.cgst         || 0,
        sgst:         data.sgst         || 0,
        igst:         data.igst         || 0,
        paid:         data.paid         || 0,
        other_description: data.other_description || '',
        other_charges: data.other_charges || 0,
        showOtherCharges: !!(data.other_charges && data.other_charges > 0),
        selectedServices: {
          'tank-cleaning': data.tank_cleaning_ids || [],
          'dry-box':       data.dry_box_ids || [],
          'ti-report':     data.ti_report_ids || [],
          'onhire':        data.onhire_ids || [],
          'ei-receipt':    data.ei_receipt_ids || [],
        },
      });

      // Set client rates
      if (data.client) {
        setClientRates({
          tank_cleaning_rate: data.tank_cleaning_rate,
          dry_box_rate: data.dry_box_rate,
          ti_report_rate: data.ti_report_rate,
          ei_receipt_rate: data.ei_receipt_rate,
          onhire_rate: data.onhire_rate,
        });
      }

      // Fetch existing service records
      await fetchExistingServices(data);

      // Fetch pending services for this client
      if (data.client_id) {
        await fetchPendingServices(data.client_id);
      }
    } catch (err) {
      Alert.error('Failed to load billing details');
      console.error(err);
      navigate('/billings');
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingServices = async (billingData) => {
    const records = {};
    
    for (const [key, cfg] of Object.entries(SERVICE_CONFIG)) {
      const ids = billingData[cfg.idField] || [];
      if (ids.length === 0) continue;

      try {
        const fetchPromises = ids.map(async (id) => {
          const fetchers = {
            tank_cleaning: tankCleaningService.getById,
            dry_box: dryBoxSurveyService.getById,
            ti_report: tiReportService.getById,
            ei_receipt: eiReceiptService.getById,
            onhire: onhireService.getById,
          };
          
          const response = await fetchers[key](id);
          return response.data;
        });

        const serviceData = await Promise.all(fetchPromises);
        records[key] = serviceData.filter(Boolean);
      } catch (err) {
        console.error(`Failed to fetch ${key} records:`, err);
        records[key] = [];
      }
    }

    setServiceRecords(records);
  };

  const fetchPendingServices = async (clientId) => {
    try {
      const response = await billingService.getPendingServices(clientId);
      const data = response || {};

      const onlyPublished = (records) => (records || []).filter((s) => s.published !== false);
      const flat = [
        ...onlyPublished(data.tank_cleanings).map(s => ({ ...s, service_type: 'tank-cleaning' })),
        ...onlyPublished(data.dry_boxes).map(s => ({ ...s, service_type: 'dry-box' })),
        ...onlyPublished(data.ti_reports).map(s => ({ ...s, service_type: 'ti-report' })),
        ...onlyPublished(data.onhires).map(s => ({ ...s, service_type: 'onhire' })),
        ...onlyPublished(data.ei_receipts).map(s => ({ ...s, service_type: 'ei-receipt' })),
      ];

      setPendingServices(flat);
    } catch (err) {
      console.error('Failed to load pending services:', err);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await locationService.getAllLocations();
      setLocations(response.data || []);
    } catch (err) {
      console.error('Failed to load locations:', err);
    }
  };

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value,
    }));
  };

  // Toggle service selection for adding new services
  const handleServiceSelect = (serviceId, serviceType) => {
    setFormData(prev => {
      const current = prev.selectedServices[serviceType] || [];
      const updated  = current.includes(serviceId)
        ? current.filter(id => id !== serviceId)
        : [...current, serviceId];
      return {
        ...prev,
        selectedServices: {
          ...prev.selectedServices,
          [serviceType]: updated,
        },
      };
    });
  };

  // Remove service from billing
  const handleRemoveService = (serviceId, serviceKey) => {
    // Check if billing has any payments
    if (formData.paid > 0) {
      Alert.error('Cannot remove services from billing with payments. Please remove all payments first.');
      return;
    }

    const cfg = SERVICE_CONFIG[serviceKey];
    const confirmMsg = `Are you sure you want to remove this ${cfg.label} service? This action cannot be undone.`;
    
    Alert.confirm(confirmMsg, 'Remove Service').then((confirmed) => {
      if (!confirmed) return;
      setFormData(prev => {
        const typeKey = cfg.key;
        const current = prev.selectedServices[typeKey] || [];
        const updated = current.filter(id => id !== serviceId);
        
        return {
          ...prev,
          selectedServices: {
            ...prev.selectedServices,
            [typeKey]: updated,
          },
        };
      });

      // Also remove from service records
      setServiceRecords(prev => {
        const updated = { ...prev };
        if (updated[serviceKey]) {
          updated[serviceKey] = updated[serviceKey].filter(s => s.id !== serviceId);
        }
        return updated;
      });

      Alert.success(`${cfg.label} service removed successfully`);
    });
  };

  // Toggle expand/collapse for service type
  const toggleServiceExpand = (serviceKey) => {
    setExpandedServices(prev => ({
      ...prev,
      [serviceKey]: !prev[serviceKey]
    }));
  };

  // ─── Derived Amounts ───────────────────────────────────────────────────────
  const computedAmounts = (() => {
    let subtotal = 0;

    Object.entries(formData.selectedServices).forEach(([type, ids]) => {
      const rateKey = SERVICE_RATE_MAP[type];
      const rate    = clientRates[rateKey] || 0;
      subtotal += ids.length * rate;
    });

    const other = parseFloat(formData.other_charges) || 0;
    const taxableSubtotal = subtotal + other;
    const cgst_amount = Math.round(taxableSubtotal * (formData.cgst / 100));
    const sgst_amount = Math.round(taxableSubtotal * (formData.sgst / 100));
    const igst_amount = Math.round(taxableSubtotal * (formData.igst / 100));
    const total       = Math.round(taxableSubtotal + cgst_amount + sgst_amount + igst_amount);
    const balance     = total - (formData.paid || 0);

    return { subtotal, taxableSubtotal, cgst_amount, sgst_amount, igst_amount, total, balance };
  })();

  const isFullyPaid = computedAmounts.balance <= 0;

  // Billing status derived
  const derivedStatus = () => {
    if (formData.paid <= 0) return { label: 'Unpaid',       cls: 'bg-red-100 text-red-700' };
    if (computedAmounts.balance > 0) return { label: 'Partial Paid', cls: 'bg-yellow-100 text-yellow-700' };
    return { label: 'Fully Paid',   cls: 'bg-green-100 text-green-700' };
  };

  const status = derivedStatus();

  // Count total selected services
  const totalSelectedCount = Object.values(formData.selectedServices).reduce(
    (sum, arr) => sum + arr.length,
    0
  );

  // ─── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.billing_date) {
      Alert.error('Billing date is required');
      return;
    }

    if (totalSelectedCount === 0) {
      Alert.error('At least one service must be selected');
      return;
    }

    try {
      setSubmitting(true);

      // Prepare payload with service-wise quantities and amounts
      const payload = {
        location_id: formData.location_id,
        billing_date: formData.billing_date,
        billing_type: formData.billing_type,
        description: formData.description,
        sac_code: formData.sac_code,
        cgst: formData.cgst,
        sgst: formData.sgst,
        igst: formData.igst,
        paid: formData.paid,
        other_description: formData.other_description || '',
        other_charges: parseFloat(formData.other_charges) || 0,
      };

      // Add service-specific data
      Object.entries(formData.selectedServices).forEach(([type, ids]) => {
        const idFieldKey = SERVICE_IDS_FIELD_MAP[type];
        payload[idFieldKey] = ids;
      });

      await billingService.update(billingId, payload);
      Alert.success('Billing updated successfully');
      navigate(`/billings/view/${billingId}`);
    } catch (err) {
      Alert.error(err.response?.data?.message || 'Failed to update billing');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-buttonBg" />
          <p className="text-sm text-gray-500">Loading billing details...</p>
        </div>
      </div>
    );
  }

  if (!billing) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-gray-600">Billing not found</p>
        </div>
      </div>
    );
  }

  const selectedLocation = locations.find(loc => loc.id === parseInt(formData.location_id));

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex-1">
          <div className="p-4 sm:p-6 lg:p-8">
            <main className="max-w-7xl mx-auto">
              {/* Page Header */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <button
                    onClick={() => navigate(`/billings/view/${billingId}`)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ArrowLeft className="w-5 h-5 text-gray-600" />
                  </button>
                  <div>
                    <h1 className="text-2xl font-bold text-secondary flex items-center gap-2">
                      <FileText className="w-6 h-6 text-buttonBg" />
                      Edit Billing
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                      Billing ID: <span className="font-semibold text-gray-700">{billing.billing_id}</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-sm shadow-sm">
                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 p-6">
                    {/* ── LEFT: Form Fields & Services ──────────────────── */}
                    <div className="xl:col-span-2 space-y-6">
                      
                      {/* Client Information (Read-only) */}
                      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                        <h2 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          Client Information (Read-only)
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <p className="text-xs text-blue-700 font-medium mb-1">Client Name</p>
                            <p className="text-sm font-semibold text-blue-900">{billing.client?.name || 'N/A'}</p>
                          </div>
                          {billing.client?.organization_name && (
                            <div>
                              <p className="text-xs text-blue-700 font-medium mb-1">Organization</p>
                              <p className="text-sm font-semibold text-blue-900">{billing.client.organization_name}</p>
                            </div>
                          )}
                          {billing.client?.contact && (
                            <div>
                              <p className="text-xs text-blue-700 font-medium mb-1 flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                Contact
                              </p>
                              <p className="text-sm font-semibold text-blue-900">{billing.client.contact}</p>
                            </div>
                          )}
                          {billing.client?.email && (
                            <div>
                              <p className="text-xs text-blue-700 font-medium mb-1 flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                Email
                              </p>
                              <p className="text-sm font-semibold text-blue-900">{billing.client.email}</p>
                            </div>
                          )}
                          {billing.client?.gst_no && (
                            <div>
                              <p className="text-xs text-blue-700 font-medium mb-1">GST No.</p>
                              <p className="text-sm font-semibold text-blue-900">{billing.client.gst_no}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Billing Details */}
                      <div className="border border-gray-100 rounded-lg p-4">
                        <h2 className="text-sm font-semibold text-buttonBg pb-2 border-b border-gray-100 mb-4">
                          Billing Details
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <FormLabel required>Billing Date</FormLabel>
                            <input
                              type="date"
                              name="billing_date"
                              value={formData.billing_date}
                              onChange={handleChange}
                              className={inputClass}
                              required
                            />
                          </div>

                          <div>
                            <FormLabel>Billing Type</FormLabel>
                            <select
                              name="billing_type"
                              value={formData.billing_type}
                              onChange={handleChange}
                              className={inputClass}
                            >
                              {BILLING_TYPES.map(type => (
                                <option key={type} value={type}>{type}</option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <FormLabel>Location</FormLabel>
                            <select
                              name="location_id"
                              value={formData.location_id}
                              onChange={handleChange}
                              className={inputClass}
                            >
                              <option value="">Select Location</option>
                              {locations.map(loc => (
                                <option key={loc.id} value={loc.id}>
                                  {loc.city}{loc.state ? `, ${loc.state}` : ''}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <FormLabel>SAC Code</FormLabel>
                            <input
                              type="text"
                              name="sac_code"
                              value={formData.sac_code}
                              onChange={handleChange}
                              className={inputClass}
                              placeholder="Enter SAC code"
                            />
                          </div>

                          <div>
                            <FormLabel>CGST %</FormLabel>
                            <input
                              type="number"
                              name="cgst"
                              value={formData.cgst}
                              onChange={handleChange}
                              className={inputClass}
                              min="0"
                              max="100"
                            />
                          </div>

                          <div>
                            <FormLabel>SGST %</FormLabel>
                            <input
                              type="number"
                              name="sgst"
                              value={formData.sgst}
                              onChange={handleChange}
                              className={inputClass}
                              min="0"
                              max="100"
                            />
                          </div>

                          <div>
                            <FormLabel>IGST %</FormLabel>
                            <input
                              type="number"
                              name="igst"
                              value={formData.igst}
                              onChange={handleChange}
                              className={inputClass}
                              min="0"
                              max="100"
                            />
                          </div>

                          <div>
                            <FormLabel>Other Description</FormLabel>
                            <div className="flex items-center gap-3 mb-2">
                              <span className="text-xs text-gray-500">Enable other charges</span>
                              <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, showOtherCharges: !prev.showOtherCharges, other_charges: prev.showOtherCharges ? 0 : prev.other_charges }))}
                                className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${formData.showOtherCharges ? 'bg-buttonBg' : 'bg-gray-300'}`}
                              >
                                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${formData.showOtherCharges ? 'translate-x-5' : 'translate-x-1'}`} />
                              </button>
                            </div>
                            {formData.showOtherCharges && (
                              <input
                                type="text"
                                name="other_description"
                                value={formData.other_description}
                                onChange={handleChange}
                                className={inputClass}
                                placeholder="e.g. Handling charges"
                              />
                            )}
                          </div>

                          <div>
                            <FormLabel>Other Charges (₹)</FormLabel>
                            {formData.showOtherCharges ? (
                              <input
                                type="number"
                                name="other_charges"
                                value={formData.other_charges}
                                onChange={handleChange}
                                className={inputClass}
                                min="0"
                                placeholder="0.00"
                              />
                            ) : (
                              <div className={`${inputClass} bg-gray-50 text-gray-400`}>—</div>
                            )}
                          </div>

                          <div className="md:col-span-2">
                            <FormLabel>Description</FormLabel>
                            <textarea
                              name="description"
                              value={formData.description}
                              onChange={handleChange}
                              className={inputClass}
                              rows="2"
                              placeholder="Add any additional notes or description"
                            />
                          </div>

                          <div>
                            <FormLabel>Amount Paid</FormLabel>
                            <input
                              type="number"
                              name="paid"
                              value={formData.paid}
                              onChange={handleChange}
                              className={inputClass}
                              min="0"
                              disabled
                            />
                            <p className="text-xs text-gray-400 mt-1">
                              <AlertCircle className="w-3 h-3 inline mr-1" />
                              Manage payments from View page
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Services Section */}
                      <div className="border border-gray-100 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-100">
                          <h2 className="text-sm font-semibold text-buttonBg flex items-center gap-2">
                            <ClipboardList className="w-4 h-4" />
                            Selected Services ({totalSelectedCount})
                          </h2>
                          {formData.paid === 0 && (
                            <button
                              type="button"
                              onClick={() => setShowAddServices(!showAddServices)}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                            >
                              {showAddServices ? (
                                <>
                                  <X className="w-3.5 h-3.5" />
                                  Close
                                </>
                              ) : (
                                <>
                                  <Plus className="w-3.5 h-3.5" />
                                  Add Services
                                </>
                              )}
                            </button>
                          )}
                        </div>

                        {/* Add Services Panel */}
                        {showAddServices && formData.paid === 0 && (
                          <div className="mb-4 p-4 bg-green-50 border border-green-100 rounded-lg">
                            <h3 className="text-sm font-semibold text-green-900 mb-3">Add Pending Services</h3>
                            
                            {/* Service Type Filter */}
                            <div className="flex gap-2 mb-3 flex-wrap">
                              {Object.entries(SERVICE_LABELS).map(([key, label]) => {
                                const filteredCount = pendingServices.filter(s => s.service_type === key).length;
                                const alreadySelected = formData.selectedServices[key]?.length || 0;
                                const availableCount = filteredCount - alreadySelected;
                                
                                return (
                                  <button
                                    key={key}
                                    type="button"
                                    onClick={() => setServiceFilter(key)}
                                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                                      serviceFilter === key
                                        ? 'bg-buttonBg text-white'
                                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                                    }`}
                                  >
                                    {label}
                                    <span className={`ml-1.5 px-1.5 py-0.5 rounded text-xs ${
                                      serviceFilter === key
                                        ? 'bg-white/20'
                                        : 'bg-gray-100'
                                    }`}>
                                      {availableCount}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>

                            {/* Service List */}
                            <div className="max-h-60 overflow-y-auto space-y-1.5">
                              {pendingServices
                                .filter(s => s.service_type === serviceFilter)
                                .filter(s => !formData.selectedServices[serviceFilter]?.includes(s.id))
                                .map(service => (
                                  <label
                                    key={service.id}
                                    className="flex items-center gap-2 p-2.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={formData.selectedServices[serviceFilter]?.includes(service.id) || false}
                                      onChange={() => handleServiceSelect(service.id, serviceFilter)}
                                      className="w-4 h-4 text-buttonBg rounded focus:ring-2 focus:ring-buttonBg/30"
                                    />
                                    <span className="text-sm font-medium text-gray-700 flex-1">
                                      {getServiceLabel(service)}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      ID: {service.id}
                                    </span>
                                  </label>
                                ))}
                              
                              {pendingServices.filter(s => s.service_type === serviceFilter && !formData.selectedServices[serviceFilter]?.includes(s.id)).length === 0 && (
                                <div className="text-center py-8 text-gray-400 text-sm">
                                  No pending {SERVICE_LABELS[serviceFilter]} services available
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Selected Services Display */}
                        <div className="space-y-3">
                          {Object.entries(SERVICE_CONFIG).map(([serviceKey, cfg]) => {
                            const ids = formData.selectedServices[cfg.key] || [];
                            if (ids.length === 0) return null;

                            const records = serviceRecords[serviceKey] || [];
                            const colors = COLOR_CLASSES[cfg.color];
                            const Icon = cfg.icon;
                            const rate = clientRates[cfg.rateField] || 0;
                            const amount = ids.length * rate;
                            const isExpanded = expandedServices[serviceKey];

                            return (
                              <div key={serviceKey} className={`border rounded-lg ${colors.border}`}>
                                {/* Service Header */}
                                <div 
                                  className={`flex items-center justify-between p-3 cursor-pointer ${colors.bg}`}
                                  onClick={() => toggleServiceExpand(serviceKey)}
                                >
                                  <div className="flex items-center gap-3 flex-1">
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors.badge}`}>
                                      <Icon className="w-4 h-4" />
                                    </div>
                                    <div>
                                      <p className="text-sm font-semibold text-gray-900">
                                        {cfg.label} ({ids.length})
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        {ids.length} × {formatCurrency(rate)}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <span className="text-sm font-bold text-gray-900">{formatCurrency(amount)}</span>
                                    {isExpanded ? (
                                      <ChevronUp className="w-4 h-4 text-gray-400" />
                                    ) : (
                                      <ChevronDown className="w-4 h-4 text-gray-400" />
                                    )}
                                  </div>
                                </div>

                                {/* Service Items */}
                                {isExpanded && (
                                  <div className="p-3 space-y-2 bg-white">
                                    {ids.map(id => {
                                      const record = records.find(r => r.id === id);
                                      return (
                                        <div 
                                          key={id}
                                          className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                        >
                                          <div className="flex-1">
                                            {record ? (
                                              <div className="space-y-1.5">
                                                <p className="text-sm font-medium text-gray-900">
                                                  {getServiceLabel(record)}
                                                </p>
                                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                                  {/* Service-specific ID fields */}
                                                  {record.container_number && (
                                                    <>
                                                      <span className="text-gray-500">Container:</span>
                                                      <span className="text-gray-700 font-medium">{record.container_number}</span>
                                                    </>
                                                  )}
                                                  {record.booking_no && (
                                                    <>
                                                      <span className="text-gray-500">Booking No:</span>
                                                      <span className="text-gray-700 font-medium">{record.booking_no}</span>
                                                    </>
                                                  )}
                                                  {record.report_id && (
                                                    <>
                                                      <span className="text-gray-500">Report ID:</span>
                                                      <span className="text-gray-700 font-medium">{record.report_id}</span>
                                                    </>
                                                  )}
                                                  {record.receipt_id && (
                                                    <>
                                                      <span className="text-gray-500">Receipt ID:</span>
                                                      <span className="text-gray-700 font-medium">{record.receipt_id}</span>
                                                    </>
                                                  )}
                                                  {record.onhire_id && (
                                                    <>
                                                      <span className="text-gray-500">Onhire ID:</span>
                                                      <span className="text-gray-700 font-medium">{record.onhire_id}</span>
                                                    </>
                                                  )}
                                                  
                                                  {/* Common fields */}
                                                  {record.location && (
                                                    <>
                                                      <span className="text-gray-500">Location:</span>
                                                      <span className="text-gray-700 font-medium">
                                                        {record.location.city}{record.location.state ? `, ${record.location.state}` : ''}
                                                      </span>
                                                    </>
                                                  )}
                                                  {record.vessel_name && (
                                                    <>
                                                      <span className="text-gray-500">Vessel:</span>
                                                      <span className="text-gray-700 font-medium">{record.vessel_name}</span>
                                                    </>
                                                  )}
                                                  {record.status && (
                                                    <>
                                                      <span className="text-gray-500">Status:</span>
                                                      <span className="text-gray-700 font-medium capitalize">{record.status}</span>
                                                    </>
                                                  )}
                                                  
                                                  {/* Type-specific date fields */}
                                                  {record.survey_date && (
                                                    <>
                                                      <span className="text-gray-500">Survey Date:</span>
                                                      <span className="text-gray-700 font-medium">{formatDate(record.survey_date)}</span>
                                                    </>
                                                  )}
                                                  {record.inspection_date && (
                                                    <>
                                                      <span className="text-gray-500">Inspection:</span>
                                                      <span className="text-gray-700 font-medium">{formatDate(record.inspection_date)}</span>
                                                    </>
                                                  )}
                                                  {record.onhire_date && (
                                                    <>
                                                      <span className="text-gray-500">Onhire Date:</span>
                                                      <span className="text-gray-700 font-medium">{formatDate(record.onhire_date)}</span>
                                                    </>
                                                  )}
                                                  
                                                  <span className="text-gray-400 col-span-2 text-[11px] pt-1 border-t border-gray-200">
                                                    Service ID: {id}
                                                  </span>
                                                </div>
                                              </div>
                                            ) : (
                                              <p className="text-sm text-gray-500">ID: {id}</p>
                                            )}
                                          </div>
                                          {formData.paid === 0 && (
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveService(id, serviceKey);
                                              }}
                                              className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                                              title="Remove service"
                                            >
                                              <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          {totalSelectedCount === 0 && (
                            <div className="text-center py-8 text-gray-400 text-sm">
                              No services selected. Click "Add Services" to select services.
                            </div>
                          )}
                        </div>

                        {/* Payment Warning */}
                        {formData.paid > 0 && (
                          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                            <div className="text-xs text-yellow-800">
                              <p className="font-semibold mb-1">Service modification restricted</p>
                              <p>This billing has payments recorded ({formatCurrency(formData.paid)}). To add or remove services, please remove all payments first from the View page.</p>
                            </div>
                          </div>
                        )}
                      </div>

                    </div>

                    {/* ── RIGHT: Live Summary + Actions ─────────────────── */}
                    <div className="xl:col-span-1 space-y-4">

                      {/* Amount Summary Card */}
                      <div className="bg-white rounded-sm shadow-sm p-4 sm:p-5 sticky top-6">
                        <h2 className="text-sm font-semibold text-buttonBg pb-2 border-b border-gray-100 mb-4">
                          Live Amount Summary
                        </h2>

                        <div className="space-y-2.5">
                          {/* Services subtotal */}
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-500">Services Subtotal</span>
                            <span className="font-semibold text-secondary">{formatCurrency(computedAmounts.subtotal)}</span>
                          </div>

                          {/* Other Charges */}
                          {(parseFloat(formData.other_charges) || 0) > 0 && (
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-gray-500">{formData.other_description || 'Other Charges'}</span>
                              <span className="font-semibold text-secondary">+ {formatCurrency(formData.other_charges)}</span>
                            </div>
                          )}

                          {/* CGST */}
                          {formData.cgst > 0 && (
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-gray-500">+ CGST ({formData.cgst}%)</span>
                              <span className="font-semibold text-orange-600">+ {formatCurrency(computedAmounts.cgst_amount)}</span>
                            </div>
                          )}

                          {/* SGST */}
                          {formData.sgst > 0 && (
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-gray-500">+ SGST ({formData.sgst}%)</span>
                              <span className="font-semibold text-orange-600">+ {formatCurrency(computedAmounts.sgst_amount)}</span>
                            </div>
                          )}

                          {/* IGST */}
                          {formData.igst > 0 && (
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-gray-500">+ IGST ({formData.igst}%)</span>
                              <span className="font-semibold text-orange-600">+ {formatCurrency(computedAmounts.igst_amount)}</span>
                            </div>
                          )}

                          {/* Total */}
                          <div className="border-t border-gray-100 pt-3">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-bold text-gray-700">Total Amount</span>
                              <span className="text-xl font-bold text-buttonBg">{formatCurrency(computedAmounts.total)}</span>
                            </div>
                          </div>

                          {/* Paid */}
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-green-600 font-medium">Amount Paid</span>
                            <span className="font-bold text-green-600">{formatCurrency(formData.paid)}</span>
                          </div>

                          {/* Balance */}
                          <div className={`flex justify-between items-center text-sm p-3 rounded-lg ${computedAmounts.balance <= 0 ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'}`}>
                            <span className={`font-bold ${computedAmounts.balance <= 0 ? 'text-green-700' : 'text-red-700'}`}>
                              Balance Due
                            </span>
                            <span className={`font-bold text-base ${computedAmounts.balance <= 0 ? 'text-green-700' : 'text-red-700'}`}>
                              {computedAmounts.balance <= 0 ? (
                                <span className="flex items-center gap-1">
                                  <CheckCircle className="w-4 h-4" />
                                  Fully Paid
                                </span>
                              ) : formatCurrency(computedAmounts.balance)}
                            </span>
                          </div>
                        </div>

                        {/* Payment Status badge */}
                        <div className="mt-4 pt-3 border-t border-gray-100">
                          <p className="text-xs text-gray-500 mb-2">Payment Status</p>
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${status.cls}`}>
                            {status.label}
                          </span>
                        </div>

                        {/* Action Buttons */}
                        <div className="mt-5 space-y-2.5">
                          <button
                            type="submit"
                            disabled={submitting || totalSelectedCount === 0}
                            className="w-full flex items-center justify-center gap-2 py-2.5 bg-buttonBg text-buttonTxt rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {submitting ? (
                              <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
                            ) : (
                              <><Save className="w-4 h-4" /> Save Changes</>
                            )}
                          </button>

                          <button
                            type="button"
                            onClick={() => navigate(`/billings/view/${billingId}`)}
                            className="w-full flex items-center justify-center gap-2 py-2.5 border border-gray-200 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                          >
                            <ArrowLeft className="w-4 h-4" />
                            Cancel
                          </button>
                        </div>

                        {/* System info */}
                        <div className="mt-4 pt-3 border-t border-gray-100 space-y-1.5">
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-400">Created by</span>
                            <span className="text-gray-600 font-medium">{billing.created_by || '—'}</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-400">Created</span>
                            <span className="text-gray-600 font-medium">{formatDate(billing.created_at)}</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-400">Last updated</span>
                            <span className="text-gray-600 font-medium">{formatDate(billing.updated_at)}</span>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                </form>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditBilling;